import { motion } from 'framer-motion';
import BannerCarousel from '@/components/home/BannerCarousel';
import TrustBadges from '@/components/home/TrustBadges';
import TopProducts from '@/components/home/TopProducts';
import RecentlyViewed from '@/components/product/RecentlyViewed';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  ChevronRight, Truck, Shield, Clock, Package, Plane
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category images - circular icons like papita.ae
const categoryImages = {
  'mobiles': 'https://images.pexels.com/photos/788946/pexels-photo-788946.jpeg?auto=compress&cs=tinysrgb&w=200',
  'laptops': 'https://images.pexels.com/photos/18105/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=200',
  'gaming': 'https://images.pexels.com/photos/3945659/pexels-photo-3945659.jpeg?auto=compress&cs=tinysrgb&w=200',
  'watches': 'https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=200',
  'perfumes': 'https://images.pexels.com/photos/965989/pexels-photo-965989.jpeg?auto=compress&cs=tinysrgb&w=200',
  'clothing': 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg?auto=compress&cs=tinysrgb&w=200',
  'accessories': 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg?auto=compress&cs=tinysrgb&w=200',
  'chocolates': 'https://images.pexels.com/photos/65882/pexels-photo-65882.jpeg?auto=compress&cs=tinysrgb&w=200',
  'electronics': 'https://images.pexels.com/photos/356056/pexels-photo-356056.jpeg?auto=compress&cs=tinysrgb&w=200',
  'home': 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=200',
  'beauty': 'https://images.pexels.com/photos/2587370/pexels-photo-2587370.jpeg?auto=compress&cs=tinysrgb&w=200',
  'shoes': 'https://images.pexels.com/photos/1598505/pexels-photo-1598505.jpeg?auto=compress&cs=tinysrgb&w=200',
  'default': 'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=200'
};

const HomePage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const getCategoryImage = (categoryId) => {
    return categoryImages[categoryId] || categoryImages.default;
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Banner Carousel */}
      <BannerCarousel />

      {/* Trust Badges Scrolling Bar */}
      <TrustBadges />

      {/* Service Banners with Background Images - Above Categories */}
      <section className="py-3 md:py-6 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Personal Shopping / Request Item Banner */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative h-[140px] sm:h-[180px] md:h-[200px] rounded-xl sm:rounded-2xl overflow-hidden cursor-pointer group"
              onClick={() => navigate('/request-item')}
              data-testid="promo-request-banner"
            >
              {/* Background Image */}
              <img 
                src="https://images.unsplash.com/photo-1761474257877-47bac32b03b6?auto=format&fit=crop&w=800&q=80"
                alt="Personal Shopping"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0047AB]/90 via-[#0047AB]/70 to-[#0047AB]/40" />
              
              {/* Content */}
              <div className="relative h-full flex flex-col justify-center p-4 sm:p-6 md:p-8">
                <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-white/80 bg-white/15 backdrop-blur-sm px-2 py-0.5 sm:px-3 sm:py-1 rounded-full w-fit mb-2 sm:mb-3">
                  <Package className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  On-Demand
                </span>
                <h3 className="text-lg sm:text-xl md:text-2xl font-outfit font-bold text-white mb-1">
                  Request Any Item
                </h3>
                <p className="text-xs sm:text-sm text-white/85 max-w-xs mb-2 sm:mb-3 line-clamp-2">
                  Can't find it? We'll source and deliver from Dubai.
                </p>
                <div className="flex items-center gap-1.5 text-white/90 text-xs sm:text-sm font-medium">
                  Get Started <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
              </div>
            </motion.div>

            {/* 3-Day Delivery Banner */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="relative h-[140px] sm:h-[180px] md:h-[200px] rounded-xl sm:rounded-2xl overflow-hidden cursor-pointer group"
              onClick={() => navigate('/delivery-info')}
              data-testid="promo-delivery-banner"
            >
              {/* Background Image */}
              <img 
                src="https://images.pexels.com/photos/32563053/pexels-photo-32563053.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Express Delivery"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-700/90 via-emerald-600/70 to-emerald-600/40" />
              
              {/* Content */}
              <div className="relative h-full flex flex-col justify-center p-4 sm:p-6 md:p-8">
                <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-white/80 bg-white/15 backdrop-blur-sm px-2 py-0.5 sm:px-3 sm:py-1 rounded-full w-fit mb-2 sm:mb-3">
                  <Plane className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  Express
                </span>
                <h3 className="text-lg sm:text-xl md:text-2xl font-outfit font-bold text-white mb-1">
                  3-Day Delivery
                </h3>
                <p className="text-xs sm:text-sm text-white/85 max-w-xs mb-2 sm:mb-3 line-clamp-2">
                  Dubai to India, guaranteed. Hassle-free customs.
                </p>
                <div className="flex items-center gap-1.5 text-white/90 text-xs sm:text-sm font-medium">
                  Learn More <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Category Quick Links - Circular icons */}
      <section className="py-4 md:py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between mb-3 sm:mb-5">
            <h2 className="text-base sm:text-lg md:text-xl font-outfit font-bold text-slate-900">
              Shop by Category
            </h2>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate('/products')}
              className="text-[#0047AB] hover:text-[#003380] hover:bg-blue-50 text-xs sm:text-sm font-medium px-2 sm:px-3"
              data-testid="view-all-categories-btn"
            >
              View All <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-0.5" />
            </Button>
          </div>
          
          {/* Horizontal Scroll on Mobile, Grid on Desktop */}
          <div className="flex md:grid md:grid-cols-6 lg:grid-cols-8 gap-3 md:gap-6 overflow-x-auto pb-2 md:pb-0 -mx-3 px-3 md:mx-0 md:px-0 scrollbar-hide">
            {categories.slice(0, 8).map((category, index) => (
              <motion.button
                key={category.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex flex-col items-center gap-1.5 sm:gap-2.5 min-w-[70px] sm:min-w-[80px] md:min-w-0 group"
                onClick={() => navigate(`/products?category=${category.id}`)}
                data-testid={`category-${category.id}`}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 border-slate-100 group-hover:border-[#0047AB] shadow-sm group-hover:shadow-md transition-all duration-300 bg-white">
                  <img 
                    src={getCategoryImage(category.id)}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <span className="text-[10px] sm:text-xs md:text-sm text-slate-700 font-medium text-center line-clamp-1 max-w-[70px] sm:max-w-[80px] md:max-w-full group-hover:text-[#0047AB] transition-colors">
                  {category.name}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Top Selling Products */}
      <TopProducts />

      {/* Recently Viewed */}
      <RecentlyViewed />

      {/* Categories Grid - Clean cards */}
      <section className="py-4 sm:py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between mb-3 sm:mb-6">
            <h2 className="text-base sm:text-lg md:text-xl font-outfit font-bold text-slate-900">
              Explore Categories
            </h2>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate('/products')}
              className="text-[#0047AB] hover:text-[#003380] hover:bg-blue-50 text-xs sm:text-sm font-medium px-2 sm:px-3"
              data-testid="view-all-explore-btn"
            >
              See All <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-0.5" />
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 md:gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ y: -4 }}
                className="relative aspect-[4/3] rounded-lg sm:rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => navigate(`/products?category=${category.id}`)}
                data-testid={`explore-category-${category.id}`}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-2.5 sm:p-4">
                  <h3 className="text-xs sm:text-sm md:text-base font-outfit font-bold text-white">
                    {category.name}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-white/70 mt-0.5 flex items-center gap-0.5">
                    Shop Now <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Shop With Us - Clean grid */}
      <section className="py-4 sm:py-8 md:py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <h2 className="text-base sm:text-lg md:text-xl font-outfit font-bold text-slate-900 text-center mb-4 sm:mb-6">
            Why Shop With DXB2BOM?
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            {[
              { icon: Truck, title: 'Fast Delivery', desc: '3-7 Days', color: 'text-blue-600 bg-blue-50' },
              { icon: Shield, title: '100% Authentic', desc: 'Genuine Products', color: 'text-green-600 bg-green-50' },
              { icon: Clock, title: '24/7 Support', desc: 'WhatsApp Help', color: 'text-purple-600 bg-purple-50' },
              { icon: Package, title: 'Safe Packaging', desc: 'Damage Protection', color: 'text-orange-600 bg-orange-50' }
            ].map((item, index) => (
              <motion.div
                key={`why-shop-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center border border-slate-100"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${item.color} rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3`}>
                  <item.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-900">{item.title}</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-6 sm:py-8 md:py-12 bg-[#0047AB]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-lg sm:text-xl md:text-2xl font-outfit font-bold text-white mb-1.5 sm:mb-2">
              Ready to Shop?
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-white/80 mb-4 sm:mb-5 max-w-md mx-auto">
              Explore premium products from Dubai delivered to your doorstep
            </p>
            <Button 
              onClick={() => navigate('/products')}
              className="bg-white text-[#0047AB] hover:bg-white/90 font-semibold shadow-lg px-5 sm:px-6 md:px-8 h-10 sm:h-11 text-sm sm:text-base"
              size="default"
            >
              Browse All Products <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
